
import { Progress } from "@/components/ui/progress";
import { Camera, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from 'react-i18next';
import { useCurrency } from "@/contexts/CurrencyContext";
import { formatCurrency } from "@/lib/utils";
import { CurrencyIcon } from "@/components/ui/currency-icon";

interface BudgetProgressProps {
  title: string;
  budget: number;
  spent: number;
  type?: "camera" | "ge" | "total";
}

export default function BudgetProgress({ title, budget, spent, type = "total" }: BudgetProgressProps) {
  const { currency } = useCurrency();
  const progress = budget > 0 ? (spent / budget) * 100 : 0;
  const remaining = budget - spent;
  const isOverBudget = budget > 0 && remaining < 0;
  const isNearBudget = progress >= 80 && progress <= 100;
  const noBudgetSet = budget === 0;
  const { t } = useTranslation();

  const getStatusColor = () => {
    if (noBudgetSet) return "text-muted-foreground";
    if (isOverBudget) return "text-destructive";
    if (isNearBudget) return "text-warning";
    return "text-success";
  };

  const getIcon = () => {
    switch (type) {
      case "camera":
        return <Camera className="h-4 w-4" />;
      case "ge":
        return <Zap className="h-4 w-4" />;
      default:
        return <CurrencyIcon className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{getIcon()}</span>
          <span className="font-medium">{t(title)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {noBudgetSet ? (
            <span className="text-muted-foreground font-medium">
              {t('budget_spent', { amount: spent?.toLocaleString() })}
            </span>
          ) : (
            <>
              <span className={cn(getStatusColor(), "font-medium")}>
                {formatCurrency(spent, currency)}
              </span>
              <span className="text-muted-foreground">
                / {formatCurrency(budget, currency)}
              </span>
            </>
          )}
        </div>
      </div>
      <Progress 
        value={noBudgetSet ? 0 : Math.min(progress, 100)} 
        className={cn(
          "h-2",
          noBudgetSet ? "bg-muted" : isOverBudget ? "bg-destructive/20" : "bg-secondary",
          progress > 0 && "transition-all duration-500"
        )}
      />
      <div className="flex justify-between text-xs">
        {noBudgetSet ? (
          <span className="text-muted-foreground">{t('no_budget_set')}</span>
        ) : (
          <>
            <span className="text-muted-foreground">
              {t('budget_used_percent', { percent: progress.toFixed(1) })}
            </span>
            <span className={cn(getStatusColor(), "font-medium")}>
              {isOverBudget ? t('over_by') : t('budget_remaining_label')} ${Math.abs(remaining).toLocaleString()}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
